import React, { useState, memo } from 'react';
import { 
  Calendar,
  Clock,
  MapPin,
  Edit2,
  Trash2,
  Copy,
  Loader2,
  Plus,
  Star
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { formatDate, formatTime } from '../../utils/dateTime';
import { isTodayPractice, isNextPractice, getPracticeStatus } from '../../utils/practiceUtils';
import type { Practice, Team } from '../../types';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import UpgradeModal from '../UpgradeModal';

interface PracticeListProps {
  practices: Practice[];
  selectedPracticeId: string | null;
  onPracticeSelect: (practice: Practice) => void;
  onEditPractice: (practice: Practice) => void;
  onDeletePractice: (practiceId: string) => void;
  onAddPractice: () => void;
  isMobileView: boolean;
  timeFormat: '12h' | '24h';
  currentTeam: Team | null;
  onRefresh?: () => void;
}

// Memoize the entire component to prevent unnecessary re-renders
const PracticeList = memo(function PracticeList({
  practices,
  selectedPracticeId,
  onPracticeSelect,
  onEditPractice,
  onDeletePractice,
  onAddPractice,
  isMobileView,
  timeFormat,
  currentTeam,
  onRefresh
}: PracticeListProps) {
  // Remove console logs
  const [isCloningPractice, setIsCloningPractice] = useState<string | null>(null);
  
  // Pro feature access for duplicate practice
  const { checkFeatureAccess, showUpgradeModal, handleUpgrade, handleCloseModal, isProUser } = useFeatureAccess();

  // Clone practice functionality
  const handleClonePractice = async (practice: Practice) => {
    // Check Pro access for duplicate practice feature
    // Using "Duplicate sessions" to avoid matching the practice limit check
    // This feature requires Pro subscription - block non-Pro users immediately
    if (!isProUser) {
      checkFeatureAccess('Duplicate sessions');
      return;
    }
    
    try {
      setIsCloningPractice(practice.id);

      // Use today's local date so the copy is clearly a new, unscheduled plan
      const today = new Date();
      const todayDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      // Create a new practice with the same data but a new ID
      const { data: newPractice, error: practiceError } = await supabase
        .from('practices')
        .insert({
          team_id: currentTeam?.id,
          date: todayDate,
          start_time: practice.start_time,
          end_time: practice.end_time,
          location: practice.location,
          theme: practice.theme,
          focus_areas: practice.focus_areas
        })
        .select()
        .single();

      if (practiceError) throw practiceError;

      // First fetch all drills from the original practice
      const { data: originalDrills, error: drillsError } = await supabase
        .from('drills')
        .select('*')
        .eq('practice_id', practice.id)
        .order('order_index');

      if (drillsError) throw drillsError;

      // Clone each drill with its position groups and steps
      for (const drill of originalDrills || []) {
        // Create new drill
        const { data: newDrill, error: newDrillError } = await supabase
          .from('drills')
          .insert({
            practice_id: newPractice.id,
            name: drill.name,
            duration: drill.duration,
            description: drill.description,
            focus_area: drill.focus_area,
            order_index: drill.order_index
          })
          .select()
          .single();

        if (newDrillError) throw newDrillError;

        // Fetch position groups for this drill
        const { data: positionGroups, error: groupsError } = await supabase
          .from('position_groups')
          .select('*')
          .eq('drill_id', drill.id);

        if (groupsError) throw groupsError;

        // Clone position groups and their steps
        for (const positionGroup of positionGroups || []) {
          // Create new position group
          const { data: newPositionGroup, error: newGroupError } = await supabase
            .from('position_groups')
            .insert({
              drill_id: newDrill.id,
              group_name: positionGroup.group_name,
              assigned_coach_id: positionGroup.assigned_coach_id
            })
            .select()
            .single();

          if (newGroupError) throw newGroupError;

          // Fetch steps for this position group
          const { data: steps, error: stepsError } = await supabase
            .from('drill_steps')
            .select('*')
            .eq('position_group_id', positionGroup.id);

          if (stepsError) throw stepsError;

          // Clone drill steps and their coaching points
          for (const step of steps || []) {
            const { data: newStep, error: newStepError } = await supabase
              .from('drill_steps')
              .insert({
                drill_id: newDrill.id,
                position_group_id: newPositionGroup.id,
                name: step.name,
                setup: step.setup,
                execution: step.execution,
                equipment: step.equipment,
                video_url: step.video_url,
                order_index: step.order_index,
                duration: step.duration
              })
              .select()
              .single();

            if (newStepError) throw newStepError;

            // Fetch coaching points for this step
            const { data: coachingPoints, error: pointsError } = await supabase
              .from('drill_coaching_points')
              .select('*')
              .eq('drill_step_id', step.id);

            if (pointsError) throw pointsError;

            // Clone coaching points
            if (coachingPoints?.length > 0) {
              const { error: coachingPointsError } = await supabase
                .from('drill_coaching_points')
                .insert(
                  coachingPoints.map(point => ({
                    drill_step_id: newStep.id,
                    point: point.point,
                    order_index: point.order_index
                  }))
                );

              if (coachingPointsError) throw coachingPointsError;
            }
          }
        }
      }

      toast.success('Practice copied successfully');
      // Clear cache and refresh practices - need to clear cache to get fresh data
      if (currentTeam?.id) {
        // Clear the practice cache for this team to ensure fresh data
        window.dispatchEvent(new CustomEvent('clear-team-cache', {
          detail: { teamId: currentTeam.id }
        }));
      }
      onRefresh?.();
    } catch (error) {
      console.error('Error copying practice:', error);
      toast.error('Failed to copy practice');
    } finally {
      setIsCloningPractice(null);
    }
  };
  
  // Empty state component
  const EmptyState = memo(() => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="bg-primary bg-opacity-10 rounded-full p-6 mb-4">
        <Calendar size={48} className="text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2">No practices yet</h3>
      <p className="text-gray-600 mb-6 max-w-md">
        Create your first practice to start organizing your team's training sessions and drills.
      </p>
      <button
        onClick={onAddPractice}
        className="btn-primary flex items-center gap-2"
      >
        <Plus size={20} />
        Create Your First Practice
      </button>
    </div>
  ));

  if (practices.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <EmptyState />
      </div>
    );
  }
  
  return (
    <div className="relative">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-4">
          {practices.map((practice, index) => {
            const isToday = isTodayPractice(practice);
            const isNext = isNextPractice(practice, practices);
            const practiceStatus = getPracticeStatus(practice);
            const isFirstPastPractice = index > 0 && 
              getPracticeStatus(practices[index - 1]).status !== 'past' && 
              practiceStatus.status === 'past';
            
                        return (
              <React.Fragment key={practice.id}>
                {isFirstPastPractice && (
                  <div className="flex items-center gap-2 py-2">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-sm text-gray-500 font-medium px-2">Past Practices</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                )}
                            <div
                className={`relative p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedPracticeId === practice.id
                    ? 'border-primary bg-primary bg-opacity-5'
                    : isToday
                    ? 'border-yellow-400 bg-yellow-50 hover:border-yellow-500 shadow-sm'
                    : 'border-gray-200 hover:border-primary hover:bg-primary hover:bg-opacity-5'
                }`}
              onClick={() => onPracticeSelect(practice)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                                     <div className="flex items-center gap-2 mb-1">
                     <h3 className="text-lg font-semibold">{practice.theme}</h3>
                     {isNext && (
                       <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                         <Star size={12} className="fill-yellow-600" />
                         {practiceStatus.status === 'today' ? 'Today' : 'Next'}
                       </div>
                     )}
                   </div>
                  <div className="flex flex-col gap-2 text-sm text-gray-600 mt-1">
                    <div className="flex items-center">
                      <Calendar size={16} className="mr-2" />
                      {formatDate(practice.date)}
                      <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                        practiceStatus.status === 'today' 
                          ? 'bg-green-100 text-green-800' 
                          : practiceStatus.status === 'upcoming'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {practiceStatus.label}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Clock size={16} className="mr-2" />
                      {formatTime(practice.start_time, timeFormat)} - {formatTime(practice.end_time, timeFormat)}
                    </div>
                    <div className="flex items-center">
                      <MapPin size={16} className="mr-2" />
                      {practice.location}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClonePractice(practice);
                    }}
                    className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isProUser ? "Clone Practice" : "Clone Practice (Pro Feature)"}
                    disabled={isCloningPractice === practice.id}
                  >
                    {isCloningPractice === practice.id ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditPractice(practice);
                    }}
                    className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary hover:bg-opacity-10"
                    title="Edit Practice"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePractice(practice.id);
                    }}
                    className="p-2 text-gray-600 hover:text-red-600 rounded-full hover:bg-red-50"
                    title="Delete Practice"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleCloseModal}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
});

export default PracticeList; 